import type { FC, Ref, MouseEvent } from 'react';
import React, { useCallback } from 'react';
import cn from 'classnames';

import { DEFAULT_BORDER_LINK, DEFAULT_BORDER_PSEUDO, DEFAULT_THEME, DEFAULT_KIND } from '../config';
import type { ILinkProps } from '../models';

import { Scroller } from './scroller';

// Дефолтная ф-ия, чтобы не писать проверок в коде компонента
// istanbul ignore next
const foo = () => {};

export const Link: FC<ILinkProps> = ({
    border,
    children,
    className,
    collectorData,
    dataTestId,
    href,
    kind = DEFAULT_KIND,
    linkRef,
    noUnderline = false,
    onClick = foo,
    onMouseEnter,
    onMouseOver,
    onMouseOut,
    pseudo = false,
    rel = 'noopener noreferrer',
    target,
    title,
    scrollOffset,
    styles,
    tabIndex,
    id,
    theme = DEFAULT_THEME,
    urlEqHref = true,
    role,
    ariaLabel,
    preventDefault = false,
}) => {
    // Совпадает ли урл страницы с указанным в href
    const isPageUrlEqHref = urlEqHref && href && collectorData?.url === href;

    const borderStyleDefault = pseudo ? DEFAULT_BORDER_PSEUDO : DEFAULT_BORDER_LINK;

    const borderStyle = border || borderStyleDefault;

    const handleClick = useCallback(
        (event: MouseEvent<HTMLSpanElement | HTMLAnchorElement>) => {
            onClick({ event });
            if (preventDefault) {
                event.preventDefault();
            }
        },
        [onClick, preventDefault]
    );

    const globalProps = {
        'data-test-id': dataTestId,
        title,
        id,
    };

    const commonProps = {
        onClick: handleClick,
        className: cn(
            styles.link,
            styles[`${theme}Theme_${kind}Kind`],
            styles[`${borderStyle}Border`],
            {
                [styles.noUnderline]: noUnderline,
            },
            className
        ),
        onMouseEnter,
        onMouseOver,
        onMouseOut,
        tabIndex,
        role,
        'aria-label': ariaLabel,
        ...globalProps,
    };

    if (isPageUrlEqHref) {
        return <span {...globalProps}>{children}</span>;
    }

    const linkContent = pseudo ? (
        <span {...commonProps} ref={linkRef}>
            {children}
        </span>
    ) : (
        <a
            {...commonProps}
            ref={linkRef as Ref<HTMLAnchorElement>}
            href={href}
            rel={rel}
            target={target}
        >
            {children}
        </a>
    );

    return scrollOffset ? <Scroller offset={scrollOffset}>{linkContent}</Scroller> : linkContent;
};
